/* eslint-disable react-refresh/only-export-components -- provider co-exports its hooks */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
  type CSSProperties,
  type ReactElement,
  type ReactNode,
} from 'react';
import { useStore } from 'zustand';

import { defaultBranding } from '@/config/branding.default';
import { defaultConfig } from '@/config/api';
import { defaultTheme, themeCssVarMap } from '@/config/theme.default';
import type { ChatBranding, ChatConfig, ChatTheme } from '@/config/types';
import { cn } from '@/lib/utils';
import { createChatApi, type ChatApi } from '@/services/chatApi';

import {
  createChatStore,
  initialChatStoreState,
  type ChatStore,
  type ChatStoreApi,
} from './state/store';
import { isInitialWelcomeOnly, makeWelcomeMessage } from './state/welcome';

export type ColorMode = 'light' | 'dark' | 'system';

interface ChatProviderProps {
  branding?: Partial<ChatBranding>;
  theme?: Partial<ChatTheme>;
  config?: Partial<ChatConfig>;
  colorMode?: ColorMode;
  children: ReactNode;
}

interface ChatContextValue {
  store: ChatStoreApi;
  api: ChatApi;
  branding: ChatBranding;
  theme: ChatTheme;
  config: ChatConfig;
  colorMode: ColorMode;
  setColorMode: (mode: ColorMode) => void;
  resolvedColorMode: 'light' | 'dark';
  chatRoot: HTMLDivElement | null;
}

const ChatContext = createContext<ChatContextValue | null>(null);

export function useChatStoreApi(): ChatStoreApi {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error('useChatStoreApi must be used inside <ChatProvider>');
  return ctx.store;
}

export function ChatProvider({
  branding,
  theme,
  config,
  colorMode: initialColorMode = 'system',
  children,
}: ChatProviderProps): ReactElement {
  const mergedBranding = useMemo<ChatBranding>(
    () => ({ ...defaultBranding, ...branding }),
    [branding],
  );
  const mergedTheme = useMemo<ChatTheme>(() => mergeTheme(defaultTheme, theme), [theme]);
  const mergedConfig = useMemo<ChatConfig>(() => ({ ...defaultConfig, ...config }), [config]);

  const [chatRoot, setChatRoot] = useState<HTMLDivElement | null>(null);

  const [colorMode, setColorModeState] = useState<ColorMode>(
    () => readStoredColorMode(mergedConfig.colorModeStorageKey) ?? initialColorMode,
  );

  const setColorMode = useCallback(
    (next: ColorMode) => {
      setColorModeState(next);
      writeStoredColorMode(mergedConfig.colorModeStorageKey, next);
    },
    [mergedConfig.colorModeStorageKey],
  );

  const resolvedColorMode = useResolvedColorMode(colorMode, mergedConfig.enableDarkMode);

  const [store] = useState<ChatStoreApi>(() =>
    createChatStore({
      ...initialChatStoreState,
      messages: [makeWelcomeMessage(mergedBranding.welcomeMessage)],
    }),
  );
  const [api] = useState<ChatApi>(() => createChatApi(mergedConfig.apiBaseUrl ?? ''));

  useEffect(() => {
    const { messages, clearChat } = store.getState();
    if (isInitialWelcomeOnly(messages)) {
      clearChat(makeWelcomeMessage(mergedBranding.welcomeMessage));
    }
  }, [store, mergedBranding.welcomeMessage]);

  const inlineStyle = useMemo<CSSProperties>(() => {
    const tokens =
      resolvedColorMode === 'dark' && mergedTheme.dark
        ? { ...mergedTheme, ...mergedTheme.dark }
        : mergedTheme;
    return buildThemeStyle(tokens, mergedBranding.fontFamily);
  }, [mergedTheme, mergedBranding.fontFamily, resolvedColorMode]);

  const ctx = useMemo<ChatContextValue>(
    () => ({
      store,
      api,
      branding: mergedBranding,
      theme: mergedTheme,
      config: mergedConfig,
      colorMode,
      setColorMode,
      resolvedColorMode,
      chatRoot,
    }),
    [
      store,
      api,
      mergedBranding,
      mergedTheme,
      mergedConfig,
      colorMode,
      setColorMode,
      resolvedColorMode,
      chatRoot,
    ],
  );

  return (
    <ChatContext value={ctx}>
      <div
        ref={setChatRoot}
        data-chat-root=""
        data-color-mode={resolvedColorMode}
        style={inlineStyle}
        className={cn(
          'text-foreground bg-background font-sans',
          resolvedColorMode === 'dark' && 'dark',
        )}
      >
        {children}
      </div>
    </ChatContext>
  );
}

export function useChatStore<T>(selector: (state: ChatStore) => T): T {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error('useChatStore must be used inside <ChatProvider>');
  return useStore(ctx.store, selector);
}

export function useChatApi(): ChatApi {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error('useChatApi must be used inside <ChatProvider>');
  return ctx.api;
}

export function useChatBranding(): ChatBranding {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error('useChatBranding must be used inside <ChatProvider>');
  return ctx.branding;
}

export function useChatConfig(): ChatConfig {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error('useChatConfig must be used inside <ChatProvider>');
  return ctx.config;
}

export function useResolvedChatColorMode(): 'light' | 'dark' {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error('useResolvedChatColorMode must be used inside <ChatProvider>');
  return ctx.resolvedColorMode;
}

interface ChatColorModeApi {
  mode: ColorMode;
  setMode: (mode: ColorMode) => void;
  resolved: 'light' | 'dark';
}

export function useChatColorMode(): ChatColorModeApi {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error('useChatColorMode must be used inside <ChatProvider>');
  return { mode: ctx.colorMode, setMode: ctx.setColorMode, resolved: ctx.resolvedColorMode };
}

export function useChatRoot(): HTMLDivElement | null {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error('useChatRoot must be used inside <ChatProvider>');
  return ctx.chatRoot;
}

function useResolvedColorMode(mode: ColorMode, enableDarkMode: boolean): 'light' | 'dark' {
  const systemPrefersDark = useSystemPrefersDark();
  if (!enableDarkMode) return 'light';
  if (mode === 'dark') return 'dark';
  if (mode === 'light') return 'light';
  return systemPrefersDark ? 'dark' : 'light';
}

function subscribePrefersDark(notify: () => void): () => void {
  if (typeof window === 'undefined' || !window.matchMedia) return () => undefined;
  const mql = window.matchMedia('(prefers-color-scheme: dark)');
  mql.addEventListener('change', notify);
  return () => {
    mql.removeEventListener('change', notify);
  };
}

function getPrefersDarkSnapshot(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function getPrefersDarkServerSnapshot(): boolean {
  return false;
}

function useSystemPrefersDark(): boolean {
  return useSyncExternalStore(
    subscribePrefersDark,
    getPrefersDarkSnapshot,
    getPrefersDarkServerSnapshot,
  );
}

const COLOR_MODES: readonly ColorMode[] = ['light', 'dark', 'system'];

function isColorMode(value: unknown): value is ColorMode {
  return typeof value === 'string' && (COLOR_MODES as readonly string[]).includes(value);
}

function readStoredColorMode(storageKey: string): ColorMode | null {
  if (!storageKey || typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(storageKey);
    return isColorMode(raw) ? raw : null;
  } catch {
    return null;
  }
}

function writeStoredColorMode(storageKey: string, mode: ColorMode): void {
  if (!storageKey || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(storageKey, mode);
  } catch {
    // localStorage may be unavailable (Safari private mode, quota); fail silently.
  }
}

function mergeTheme(base: ChatTheme, overrides?: Partial<ChatTheme>): ChatTheme {
  if (!overrides) return base;
  const { dark: darkOverrides, ...rest } = overrides;
  const merged: ChatTheme = { ...base, ...rest };
  const dark = darkOverrides ? { ...base.dark, ...darkOverrides } : base.dark;
  if (dark) merged.dark = dark;
  return merged;
}

function buildThemeStyle(theme: ChatTheme, fontFamily?: string): CSSProperties {
  const style: Record<string, string> = {};
  for (const [key, cssVar] of Object.entries(themeCssVarMap)) {
    const value = theme[key as keyof typeof themeCssVarMap];
    if (value) style[cssVar] = value;
  }
  if (fontFamily) style['--font-sans'] = fontFamily;
  return style;
}
